<template>
  <div class="view-container paid-invoices">
    <PageHeader
      title="صورتحساب‌های پرداختی"
      infoTitle="راهنمای صورتحساب‌های پرداختی"
      :infoLearnUrl="infoLearnUrl"
    >
      <template #actions>
        <div class="header-actions">
          <button class="btn btn-ghost" @click="goBack">بازگشت</button>
        </div>
      </template>
      <template #info>
        <p>
          در این صفحه، صورتحساب‌های پرداخت‌شده‌ی شما نمایش داده می‌شود. با باز کردن هر ردیف، تراکنش‌های مربوط به همان
          صورتحساب را می‌توانید مشاهده کنید.
        </p>
      </template>
    </PageHeader>

    <div class="toolbar">
      <input
        v-model="search"
        class="rtl-input search-input"
        type="text"
        placeholder="جستجو در شماره صورتحساب، عنوان یا وضعیت..."
      />
      <button class="btn btn-primary" @click="reload" :disabled="loading">
        <span v-if="loading">در حال دریافت...</span>
        <span v-else>بروزرسانی</span>
      </button>
    </div>

    <div v-if="error" class="error-box">
      {{ error }}
    </div>

    <HierarchyTable
      :items="tableItems"
      id-field="id"
      parent-id-field="parentId"
      :search="search"
      :search-keys="['title', 'invoiceNumber', 'status', 'amountText', 'dateText', 'refId']"
      :default-expanded-ids="[]"
      empty-text="صورتحساب پرداخت‌شده‌ای برای نمایش وجود ندارد."
      class="mt-2"
    >
      <template #head>
        <th style="width: 34%;">عنوان / شماره</th>
        <th style="width: 18%;">تاریخ</th>
        <th style="width: 18%;">مبلغ</th>
        <th style="width: 15%;">وضعیت</th>
        <th style="width: 15%;">جزئیات</th>
      </template>

      <template #row="{ item: row, toggleExpand, indentPx }">
        <td>
          <div class="cell-title" :style="{ paddingRight: indentPx + 'px' }">
            <button
              v-if="row.hasChildren"
              class="expand-btn"
              @click.stop="toggleExpand(row.id)"
              :aria-label="row.isExpanded ? 'بستن' : 'باز کردن'"
            >
              <ArrowRightSvg class="expand-icon" :class="{ 'is-open': row.isExpanded }" aria-hidden="true" />
            </button>

            <div class="title-stack">
              <div class="title-main">
                <span class="badge" :class="row.kind === 'invoice' ? 'badge-invoice' : 'badge-transaction'">
                  {{ row.kind === 'invoice' ? 'صورتحساب' : 'تراکنش' }}
                </span>
                <strong>{{ row.title }}</strong>
              </div>
              <div class="title-sub" v-if="row.invoiceNumber || row.refId">
                <span v-if="row.invoiceNumber">شماره: {{ row.invoiceNumber }}</span>
                <span v-if="row.refId" class="sep">|</span>
                <span v-if="row.refId">کد پیگیری: {{ row.refId }}</span>
              </div>
            </div>
          </div>
        </td>

        <td>{{ row.dateText || '-' }}</td>
        <td>{{ row.amountText || '-' }}</td>
        <td>
          <span class="status" :class="statusClass(row.status)">{{ row.status || '-' }}</span>
        </td>
        <td>
          <button v-if="row.kind === 'invoice'" class="btn btn-ghost" @click.stop="copyInvoiceNumber(row)">
            کپی شماره
          </button>
        </td>
      </template>
    </HierarchyTable>

    <div v-if="showMockHint" class="hint-box">
      به نظر می‌رسه API صورتحساب‌ها هنوز در پروژه وصل نشده؛ داده‌های نمونه نمایش داده شدند.
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'

import PageHeader from '@/components/PageHeader.vue'
import HierarchyTable from '@/components/UiCompunents/HierarchyTable.vue'
import ArrowRightSvg from '@/components/svgs/ArrowRightSvg.vue'

// NOTE: This project has two axios instances (`src/axios.js` and `src/stores/api.js`).
// Most stores use `src/axios.js` (apiClient with env baseURL). We'll use that here.
import apiClient from '@/axios.js'

const router = useRouter()
const toast = useToast()

const infoLearnUrl = ref('https://www.sinapardazesh.ir/')

const loading = ref(false)
const error = ref('')
const search = ref('')

// flat list for HierarchyTable (invoice rows + child transaction rows)
const items = ref([])
const showMockHint = ref(false)

function goBack() {
  router.back()
}

function statusClass(status) {
  const s = String(status || '').toLowerCase()
  if (s.includes('موفق') || s.includes('paid') || s.includes('success')) return 'is-success'
  if (s.includes('ناموفق') || s.includes('fail')) return 'is-danger'
  if (s.includes('در انتظار') || s.includes('pending')) return 'is-warning'
  return 'is-muted'
}

function normalizeMoney(val) {
  if (val == null || val === '') return null
  const n = Number(val)
  if (Number.isNaN(n)) return null
  return n
}

function formatMoneyIRR(amount) {
  if (amount == null) return '-'
  try {
    // keep it simple, no locale deps
    return `${Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} تومان`
  } catch {
    return `${amount} تومان`
  }
}

function safeDateText(v) {
  if (!v) return ''
  // if backend sends ISO string
  const s = String(v)
  // keep it raw (project uses Persian date pickers elsewhere; we avoid adding dependency here)
  return s
}

function toFlatHierarchy(invoices, txByInvoice) {
  const flat = []
  for (const inv of invoices) {
    const id = `inv-${inv.id ?? inv.factorId ?? inv.invoiceId ?? inv.number ?? Math.random().toString(16).slice(2)}`
    const invNumber = inv.invoiceNumber ?? inv.number ?? inv.factorNumber ?? inv.code
    const invAmount = normalizeMoney(inv.amount ?? inv.totalAmount ?? inv.price)

    flat.push({
      id,
      parentId: null,
      kind: 'invoice',
      title: inv.title ?? inv.description ?? (invNumber ? `صورتحساب ${invNumber}` : 'صورتحساب'),
      invoiceNumber: invNumber ? String(invNumber) : '',
      dateText: safeDateText(inv.paidAt ?? inv.date ?? inv.createdAt),
      amountText: formatMoneyIRR(invAmount),
      status: inv.status ?? inv.state ?? 'پرداخت شده',
      hasChildren: (txByInvoice.get(id) || []).length > 0,
    })

    const txs = txByInvoice.get(id) || []
    for (const tx of txs) {
      const txId = `tx-${tx.id ?? tx.transactionId ?? tx.refId ?? Math.random().toString(16).slice(2)}`
      const txAmount = normalizeMoney(tx.amount ?? tx.price ?? tx.paidAmount)
      flat.push({
        id: txId,
        parentId: id,
        kind: 'transaction',
        title: tx.title ?? tx.gateway ?? 'تراکنش',
        refId: tx.refId ?? tx.referenceId ?? tx.trackingCode ?? '',
        dateText: safeDateText(tx.date ?? tx.paidAt ?? tx.createdAt),
        amountText: formatMoneyIRR(txAmount),
        status: tx.status ?? tx.state ?? 'موفق',
        hasChildren: false,
      })
    }
  }
  return flat
}

// API assumptions (can be adjusted later):
// - GET /Invoice/Paid (returns invoices)
// - GET /Invoice/{id}/Transactions OR /Transaction/ByInvoice/{id}
async function fetchPaidInvoices() {
  loading.value = true
  error.value = ''
  showMockHint.value = false

  try {
    // Try a couple of likely endpoints (swagger-front project, not sure of exact path)
    let invoices = null
    const candidates = ['/Invoice/Paid', '/Invoices/Paid', '/Billing/PaidInvoices', '/Factor/Paid']

    for (const url of candidates) {
      try {
        const res = await apiClient.get(url)
        invoices = res?.data
        if (invoices) break
      } catch {
        // try next
      }
    }

    if (!Array.isArray(invoices)) {
      // fallback: show mock so UI is ready
      showMockHint.value = true
      invoices = [
        { id: 101, invoiceNumber: 'INV-101', title: 'تمدید سامانه', amount: 1200000, paidAt: '1404/11/25', status: 'پرداخت شده' },
        { id: 102, invoiceNumber: 'INV-102', title: 'خرید فضای اضافه', amount: 350000, paidAt: '1404/11/20', status: 'پرداخت شده' },
      ]
    }

    // transactions fetching (best effort)
    const txByInvoice = new Map()

    for (const inv of invoices) {
      const invIdRaw = inv.id ?? inv.factorId ?? inv.invoiceId
      const rowId = `inv-${invIdRaw ?? inv.invoiceNumber ?? inv.number ?? inv.code}`

      let txs = []
      if (invIdRaw != null && !showMockHint.value) {
        const txCandidates = [`/Invoice/${invIdRaw}/Transactions`, `/Transaction/ByInvoice/${invIdRaw}`, `/Factor/${invIdRaw}/Transactions`]
        for (const url of txCandidates) {
          try {
            const res = await apiClient.get(url)
            if (Array.isArray(res?.data)) {
              txs = res.data
              break
            }
          } catch {
            // ignore
          }
        }
      }

      if (showMockHint.value) {
        txs = [
          { id: 1, title: 'درگاه آنلاین', refId: 'A1B2C3', amount: inv.amount, date: inv.paidAt, status: 'موفق' },
        ]
      }

      txByInvoice.set(rowId, txs)
    }

    items.value = toFlatHierarchy(invoices, txByInvoice)
  } catch (e) {
    console.error(e)
    error.value = 'خطا در دریافت صورتحساب‌ها. لطفاً دوباره تلاش کنید.'
    items.value = []
  } finally {
    loading.value = false
  }
}

function reload() {
  fetchPaidInvoices()
}

async function copyInvoiceNumber(row) {
  try {
    const text = row?.invoiceNumber || ''
    if (!text) return
    await navigator.clipboard.writeText(text)
    toast.success('شماره صورتحساب کپی شد')
  } catch {
    toast.info('امکان کپی وجود ندارد')
  }
}

const tableItems = computed(() => items.value)

onMounted(() => {
  fetchPaidInvoices()
})
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 10px;
}

.search-input {
  min-width: 280px;
  max-width: 520px;
  flex: 1;
  height: 42px;
}

.cell-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.title-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-sub {
  opacity: 0.8;
  font-size: 12px;
}

.title-sub .sep {
  margin: 0 6px;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border-color);
}

.badge-invoice {
  /* use theme primary color (falls back to blue) */
  background: rgba(var(--background-color-button-rgb, 20,116,242), 0.08);
  color: var(--text-color-primary);
}

.badge-transaction {
  /* use a success-ish fallback; consumer theme can provide --text-color-success or RGB var */
  background: rgba(var(--text-color-success-rgb, 16,185,129), 0.08);
  color: var(--text-color-success, #0f766e);
}

.status {
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border-color);
  display: inline-block;
}

.status.is-success {
  color: var(--text-color-success, #0f766e);
  background: rgba(var(--text-color-success-rgb, 16,185,129), 0.10);
  border-color: rgba(var(--text-color-success-rgb, 16,185,129), 0.25);
}

.status.is-danger {
  color: var(--text-color-danger, #b91c1c);
  background: rgba(var(--text-color-danger-rgb, 239,68,68), 0.10);
  border-color: rgba(var(--text-color-danger-rgb, 239,68,68), 0.25);
}

.status.is-warning {
  color: var(--text-color-warning, #92400e);
  background: rgba(var(--text-color-warning-rgb, 245,158,11), 0.12);
  border-color: rgba(var(--text-color-warning-rgb, 245,158,11), 0.30);
}

.status.is-muted {
  opacity: 0.85;
}

.error-box {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(var(--text-color-danger-rgb, 185,28,28), 0.35);
  background: rgba(var(--text-color-danger-rgb, 185,28,28), 0.08);
  color: var(--text-color-danger, #b91c1c);
}

.hint-box {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(var(--background-color-button-rgb, 20,116,242), 0.35);
  background: rgba(var(--background-color-button-rgb, 20,116,242), 0.08);
  color: var(--text-color-primary);
  font-size: 13px;
}

.expand-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.expand-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.18s ease;
}

.expand-icon.is-open {
  transform: rotate(90deg);
}
</style>
